import React, { useState, useMemo } from 'react';
import {
  Box,
  Button,
  Grid,
  IconButton,
  TextField,
  MenuItem,
  Tooltip,
} from '@mui/material';
import PageWrapper from 'components/Common/PageWraper';
import PageHeader from 'components/Header/Page';
import { CustomizedDataGrid } from 'components/customized_data_grid';
import RowTypography from 'components/Table/RowTypography';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as EditIcon } from '../../assets/PepWise2.0/edit-img.svg';
import { ReactComponent as DeleteIcon } from '../../assets/PepWise2.0/delete-img.svg';
import StatusChip from 'components/Table/StatusChip';


type RequestStatus = 'PENDING' | 'COMPLETED' | 'IN PROGRESS';

interface ProductRow {
  id: number;
  publisher: string;
  productName: string;
  purchaseType: 'Direct With Publisher' | 'Through Reseller';
  vendor: string;
  vendorContact: string;
  cnda: string; // could be '-' or 'Yes/No'
  msa: string; // could be '-' or 'Yes/No'
  requestStatus: RequestStatus;
}

interface FilterInfo {
  [key: string]: string[];
}

interface SortingInfo {
  sortBy: string;
  sortDir: string;
}

const editFieldSx = {
  width: '100%',
  '& .MuiOutlinedInput-root': {
    height: 28,
    boxSizing: 'border-box',
    overflow: 'hidden',
    backgroundColor: 'transparent',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#000',
    borderWidth: '1px',
  },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#000',
  },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#000',
  },

  // input text
  '& .MuiInputBase-input': {
    color: '#000',
    padding: '4px 10px',
    height: '100%',
    boxSizing: 'border-box',
  },

  // select
  '& .MuiSelect-select': {
    display: 'flex',
    alignItems: 'center',
    paddingTop: '4px',
    paddingBottom: '4px',
  },
  '& .MuiSvgIcon-root': { color: '#000' },
};

// --------------------------------------------------------
// MOCK DATA
// --------------------------------------------------------
const mockData: ProductRow[] = [
  {
    id: 1,
    publisher: 'Open Text',
    productName: 'Voltage',
    purchaseType: 'Direct With Publisher',
    vendor: 'Open Text',
    vendorContact: 'vendor@pepsico.com',
    cnda: '-',
    msa: '-',
    requestStatus: 'IN PROGRESS',
  },
  {
    id: 2,
    publisher: 'Databricks',
    productName: 'Unity Catalogue',
    purchaseType: 'Direct With Publisher',
    vendor: 'Databricks',
    vendorContact: 'vendor@pepsico.com',
    cnda: '-',
    msa: '-',
    requestStatus: 'IN PROGRESS',
  },
  {
    id: 3,
    publisher: 'Trifacta',
    productName: 'Trifacta',
    purchaseType: 'Through Reseller',
    vendor: 'Trifacta',
    vendorContact: 'vendor@pepsico.com',
    cnda: '-',
    msa: '-',
    requestStatus: 'IN PROGRESS',
  },
  {
    id: 4,
    publisher: 'TeraForm',
    productName: 'TeraForm',
    purchaseType: 'Direct With Publisher',
    vendor: 'TeraForm',
    vendorContact: 'vendor@pepsico.com',
    cnda: '-',
    msa: '-',
    requestStatus: 'IN PROGRESS',
  },
  {
    id: 5,
    publisher: 'Teradata',
    productName: 'Teradata',
    purchaseType: 'Direct With Publisher',
    vendor: 'Teradata',
    vendorContact: 'vendor@pepsico.com',
    cnda: '-',
    msa: '-',
    requestStatus: 'IN PROGRESS',
  },
  {
    id: 6,
    publisher: 'Tellius',
    productName: 'Tellius',
    purchaseType: 'Through Reseller',
    vendor: 'Tellius',
    vendorContact: 'vendor@pepsico.com',
    cnda: '-',
    msa: '-',
    requestStatus: 'IN PROGRESS',
  },
  {
    id: 7,
    publisher: 'SalesForce',
    productName: 'Tableau',
    purchaseType: 'Direct With Publisher',
    vendor: 'SalesForce',
    vendorContact: 'vendor@pepsico.com',
    cnda: '-',
    msa: '-',
    requestStatus: 'IN PROGRESS',
  },
  {
    id: 8,
    publisher: 'Microsoft',
    productName: 'Synapse',
    purchaseType: 'Through Reseller',
    vendor: 'Microsoft',
    vendorContact: 'vendor@pepsico.com',
    cnda: '-',
    msa: '-',
    requestStatus: 'COMPLETED',
  },
  {
    id: 9,
    publisher: 'Starburst',
    productName: 'Starburst Enterprise Elite',
    purchaseType: 'Direct With Publisher',
    vendor: 'Starburst',
    vendorContact: 'vendor@pepsico.com',
    cnda: '-',
    msa: '-',
    requestStatus: 'COMPLETED',
  },
  {
    id: 10,
    publisher: 'Starburst',
    productName: 'Starburst Enterprise',
    purchaseType: 'Through Reseller',
    vendor: 'Starburst',
    vendorContact: 'vendor@pepsico.com',
    cnda: '-',
    msa: '-',
    requestStatus: 'COMPLETED',
  },
];

const defaultColumnNames = [
  'Publisher',
  'Product Name',
  'Purchase Type',
  'Vendor',
  'Vendor Contact',
  'CNDA',
  'MSA',
  'Request Status',
  'Actions',
];

export default function TableDemoPage() {
  const navigate = useNavigate();

  const [rows, setRows] = useState<ProductRow[]>([...mockData]);

  const [filterInfo, setFilterInfo] = useState<FilterInfo>({});
  const [sortingInfo, setSortingInfo] = useState<SortingInfo>({ sortBy: '', sortDir: '' });

  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  const [searchData, setSearchData] = useState<any>({});
  const [tableLoading, setTableLoading] = useState(false);

  // Row editing state
  const [editingRowId, setEditingRowId] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState<Partial<ProductRow>>({});

  // Delay helper
  const wait = (ms: number) => new Promise((res) => setTimeout(res, ms));

  // --------------------------------------------------------
  // FILTER OPTIONS
  // --------------------------------------------------------
  const filterDataOptions = useMemo(() => {
    const uniq = (arr: string[]) => Array.from(new Set(arr));
    return {
      publisher: uniq(rows.map((x) => x.publisher)),
      productName: uniq(rows.map((x) => x.productName)),
      purchaseType: uniq(rows.map((x) => x.purchaseType)),
      vendor: uniq(rows.map((x) => x.vendor)),
      vendorContact: uniq(rows.map((x) => x.vendorContact)),
      cnda: uniq(rows.map((x) => x.cnda)),
      msa: uniq(rows.map((x) => x.msa)),
      requestStatus: uniq(rows.map((x) => x.requestStatus)),
    };
  }, [rows]);

  // --------------------------------------------------------
  // FILTER WITH LOADING
  // --------------------------------------------------------
  const handleFilter = async (newFilterInfo: FilterInfo) => {
    setTableLoading(true);
    await wait(400);

    let data: ProductRow[] = [...rows];

    Object.entries(newFilterInfo).forEach(([key, values]) => {
      if (Array.isArray(values) && values.length > 0) {
        const columnKeyMap: Record<string, keyof ProductRow> = {
          Publisher: 'publisher',
          'Product Name': 'productName',
          'Purchase Type': 'purchaseType',
          Vendor: 'vendor',
          'Vendor Contact': 'vendorContact',
          CNDA: 'cnda',
          MSA: 'msa',
          'Request Status': 'requestStatus',
        };

        const dataKey = (columnKeyMap[key] as keyof ProductRow) || (key as keyof ProductRow);

        data = data.filter((item) => {
          const itemValue = item[dataKey];
          return values.some((filterValue) =>
            String(itemValue).toLowerCase().includes(String(filterValue).toLowerCase()),
          );
        });
      }
    });

    setFilterInfo(newFilterInfo);
    setFilteredData(data);
    setPage(1);
    setTableLoading(false);
  };

  // store filtered separately like your old pattern (so sorting/paging reuses it)
  const [filteredData, setFilteredData] = useState<ProductRow[]>([...mockData]);

  // keep filtered in sync if rows change (delete/save edits)
  React.useEffect(() => {
    // re-apply existing filters on rows when rows mutate
    const applyFilters = () => {
      let data: ProductRow[] = [...rows];
      Object.entries(filterInfo).forEach(([key, values]) => {
        if (Array.isArray(values) && values.length > 0) {
          const columnKeyMap: Record<string, keyof ProductRow> = {
            Publisher: 'publisher',
            'Product Name': 'productName',
            'Purchase Type': 'purchaseType',
            Vendor: 'vendor',
            'Vendor Contact': 'vendorContact',
            CNDA: 'cnda',
            MSA: 'msa',
            'Request Status': 'requestStatus',
          };
          const dataKey = (columnKeyMap[key] as keyof ProductRow) || (key as keyof ProductRow);
          data = data.filter((item) =>
            values.some((v) => String(item[dataKey]).toLowerCase().includes(String(v).toLowerCase())),
          );
        }
      });
      return data;
    };
    setFilteredData(applyFilters());
  }, [rows]);

  // --------------------------------------------------------
  // SORTING
  // --------------------------------------------------------
  const sortedData = useMemo(() => {
    if (!sortingInfo.sortBy) return filteredData;

    return [...filteredData].sort((a, b) => {
      const field = sortingInfo.sortBy as keyof ProductRow;
      const dir = sortingInfo.sortDir === 'asc' ? 1 : -1;

      const aField = a[field];
      const bField = b[field];

      return String(aField).localeCompare(String(bField)) * dir;
    });
  }, [sortingInfo, filteredData]);

  // --------------------------------------------------------
  // PAGINATION
  // --------------------------------------------------------
  const paginatedData = useMemo(() => {
    const start = (page - 1) * pageSize;
    return sortedData.slice(start, start + pageSize);
  }, [sortedData, page, pageSize]);

  const paginationData = {
    currentPage: page,
    totalPages: Math.max(1, Math.ceil(sortedData.length / pageSize)),
    totalElements: sortedData.length,
    pageSize: pageSize,
  };

  // --------------------------------------------------------
  // EDIT / DELETE
  // --------------------------------------------------------
  const startEdit = (row: ProductRow) => {
    setEditingRowId(row.id);
    setEditDraft({ ...row });
  };

  const saveEdit = async () => {
    if (editingRowId == null) return;

    setTableLoading(true);
    await wait(250);

    setRows((prev) =>
      prev.map((r) => (r.id === editingRowId ? ({ ...r, ...(editDraft as ProductRow) } as ProductRow) : r)),
    );

    setEditingRowId(null);
    setEditDraft({});
    setTableLoading(false);
  };

  const deleteRow = async (id: number) => {
    setTableLoading(true);
    await wait(250);

    setRows((prev) => prev.filter((r) => r.id !== id));

    // if you delete last row on last page, keep page valid
    setPage((p) => {
      const newTotal = sortedData.length - 1;
      const newTotalPages = Math.max(1, Math.ceil(newTotal / pageSize));
      return Math.min(p, newTotalPages);
    });

    // if deleting the editing row, exit edit mode
    if (editingRowId === id) {
      setEditingRowId(null);
      setEditDraft({});
    }

    setTableLoading(false);
  };

  const getColumns = () => {
    const isEditing = (row: ProductRow) => row.id === editingRowId;

    const textCell = (row: ProductRow, field: keyof ProductRow) => {
      if (!isEditing(row)) {
        return <RowTypography>{row[field] ? String(row[field]) : '--'}</RowTypography>;
      }

      return (
        <TextField
          size="small"
          variant="outlined"
          value={(editDraft[field] ?? '') as any}
          onChange={(e) => setEditDraft((d) => ({ ...d, [field]: e.target.value }))}
          fullWidth
          sx={editFieldSx}
        />
      );
    };

    const selectCell = (
      row: ProductRow,
      field: keyof ProductRow,
      options: string[],
    ) => {
      if (!isEditing(row)) {
        return <RowTypography>{row[field] ? String(row[field]) : '--'}</RowTypography>;
      }

      return (
        <TextField
          select
          size="small"
          variant="outlined"
          value={(editDraft[field] ?? '') as any}
          onChange={(e) => setEditDraft((d) => ({ ...d, [field]: e.target.value }))}
          fullWidth
          sx={editFieldSx}
        >
          {options.map((opt) => (
            <MenuItem key={opt} value={opt}>
              {opt}
            </MenuItem>
          ))}
        </TextField>
      );
    };

    return [
      {
        key: 'publisher',
        name: 'Publisher',
        formatter: ({ row }: { row: ProductRow }) => textCell(row, 'publisher'),
        minWidth: 160,
        resizable: true,
        flexGrow: 1,
      },
      {
        key: 'productName',
        name: 'Product Name',
        formatter: ({ row }: { row: ProductRow }) => textCell(row, 'productName'),
        minWidth: 190,
        resizable: true,
        flexGrow: 1,
      },
      {
        key: 'purchaseType',
        name: 'Purchase Type',
        formatter: ({ row }: { row: ProductRow }) =>
          selectCell(row, 'purchaseType', ['Direct With Publisher', 'Through Reseller']),
        minWidth: 190,
        resizable: true,
        flexGrow: 1,
      },
      {
        key: 'vendor',
        name: 'Vendor',
        formatter: ({ row }: { row: ProductRow }) => textCell(row, 'vendor'),
        minWidth: 160,
        resizable: true,
        flexGrow: 1,
      },
      {
        key: 'vendorContact',
        name: 'Vendor Contact',
        formatter: ({ row }: { row: ProductRow }) => textCell(row, 'vendorContact'),
        minWidth: 220,
        resizable: true,
        flexGrow: 1,
      },
      {
        key: 'cnda',
        name: 'CNDA',
        formatter: ({ row }: { row: ProductRow }) =>
          selectCell(row, 'cnda', ['-', 'Yes', 'No']),
        minWidth: 100,
        resizable: true,
        flexGrow: 0.6,
      },
      {
        key: 'msa',
        name: 'MSA',
        formatter: ({ row }: { row: ProductRow }) =>
          selectCell(row, 'msa', ['-', 'Yes', 'No']),
        minWidth: 100,
        resizable: true,
        flexGrow: 0.6,
      },
      {
        key: 'requestStatus',
        name: 'Request Status',
        formatter: ({ row }: { row: ProductRow }) =>
          isEditing(row)
            ? selectCell(row, 'requestStatus', ['PENDING', 'IN PROGRESS', 'COMPLETED'])
            : (
              <StatusChip
                status={row.requestStatus}
                columnName="requestStatus"
              />
            ),
        minWidth: 160,
        resizable: true,
        flexGrow: 0.8,
      },
      {
        key: 'actions',
        name: 'Actions',
        minWidth: 120,
        resizable: false,
        formatter: ({ row }: { row: ProductRow }) => {
          const editing = row.id === editingRowId;

          return (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Tooltip title={editing ? 'Save' : 'Edit'}>
                  <EditIcon 
                      style={{ cursor: 'pointer' }}
                      // onClick={(e) => {
                      //   e.stopPropagation();
                      //   if (editing) {
                      //     saveEdit();
                      //   } else {
                      //     startEdit(row);
                      //   }
                      // }}
                  />
              </Tooltip>

              <Tooltip title="Delete">
                  <DeleteIcon 
                    style={{ cursor: 'pointer' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteRow(row.id);
                    }}
                  />
              </Tooltip>
            </Box>
          );
        },
      },
    ];
  };

  return (
    <PageWrapper>
      <PageHeader title="All Products">
        <Button variant="pepwiseSecondary" onClick={() => navigate(-1)}>
          ← Back
        </Button>
      </PageHeader>

      <Grid container spacing={3} mt={2}>
        <Grid item xs={12}>
          <Box sx={{ height: 600, mt: 2 }}>
            <CustomizedDataGrid
              title="All Products"
              tableColumns={getColumns()}
              tableData={paginatedData}
              tableType="all-products"
              defaultColumnNames={defaultColumnNames}
              onSorting={async (info: SortingInfo) => {
                setTableLoading(true);
                await wait(300);
                setSortingInfo(info);
                setPage(1);
                setTableLoading(false);
              }}
              currSorting={sortingInfo}
              filterDataOptions={filterDataOptions}
              currFilter={filterInfo}
              onFilterCheck={handleFilter}
              isFetching={tableLoading}
              searchDataValues={searchData}
              onFilterSearch={(searchDetails: any, columnKey: string) => {
                const newSearchData = { ...searchData };
                if (!searchDetails || searchDetails.length === 0) {
                  delete newSearchData[columnKey];
                } else {
                  newSearchData[columnKey] = searchDetails;
                }
                setSearchData(newSearchData);

                const newFilterInfo = { ...filterInfo };
                if (!searchDetails || searchDetails.length === 0) {
                  delete newFilterInfo[columnKey];
                } else {
                  newFilterInfo[columnKey] = searchDetails;
                }
                setFilterInfo(newFilterInfo);
                setPage(1);
              }}
              page={page}
              pageSize={pageSize}
              paginationData={paginationData}
              handlepageSizeChange={(e: React.ChangeEvent<{ value: unknown }>) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
              handlePageChange={(event: React.ChangeEvent<unknown>, newPage: number) => setPage(newPage)}
              pageSizeOptions={[10, 50, 100]}
              listLength={paginatedData.length}
              isDisabled={false}
              isDateRangeFilter={false}
              showExport={true}
              exportTableData={async () => console.log('Exporting data...')}
              exportTableDataCSV={async () => console.log('Exporting CSV...')}
              onCellClick={() => {}}
              selectedRowId={null}
              onHeaderSelectAll={() => {}}
              onFilterClick={() => {}}
            />
          </Box>
        </Grid>
      </Grid>
    </PageWrapper>
  );
}